// App.js with React Router
import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import Navigation from './components/Navigation';
import Footer from './components/Footer';
import HomePage from './pages/HomePage';
import TidioChat from './components/TidioChat';
import CookieBanner from './components/CookieBanner';
import CookieSettingsModal from './components/CookieSettingsModal';
import { CookieConsentProvider, useCookieConsent } from './contexts/CookieConsentContext';
import './App.css';

// Lazy-load non-homepage routes
const SignupPage = lazy(() => import('./pages/SignupPage'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const DocumentationPage = lazy(() => import('./pages/DocumentationPage'));
const EnterpriseDeploymentPage = lazy(() => import('./pages/EnterpriseDeploymentPage'));
const AppUnderTest = lazy(() => import('./pages/appundertest/AppUnderTest'));
const BlogListPage = lazy(() => import('./pages/BlogListPage'));
const BlogPostPage = lazy(() => import('./pages/BlogPostPage'));
const UseCaseIndexPage = lazy(() => import('./pages/UseCaseIndexPage'));
const UseCasePage = lazy(() => import('./pages/UseCasePage'));
const VideoPlayer = lazy(() => import('./pages/VideoPlayer'));
const DemosIndexPage = lazy(() => import('./pages/DemosIndexPage'));
const DemoPage = lazy(() => import('./pages/DemoPage'));

const AppContent = () => {
  const location = useLocation();
  const { showSettings, closeSettings } = useCookieConsent();
  const isDocumentationPage = location.pathname === '/documentation';
  const isEnterpriseDeploymentPage = location.pathname === '/enterprise-deployment';
  const isAppUnderTest = location.pathname.startsWith('/appundertest');
  const isVideoPlayer = location.pathname === '/video';
  const hideChrome = isDocumentationPage || isEnterpriseDeploymentPage || isAppUnderTest || isVideoPlayer;

  // Scroll to hash anchor after page renders
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      const tryScroll = (attempts = 0) => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth' });
        } else if (attempts < 10) {
          setTimeout(() => tryScroll(attempts + 1), 200);
        }
      };
      setTimeout(() => tryScroll(), 100);
    }
  }, [location]);

  // Scroll to top on route (path) changes that aren't anchor jumps, so moving
  // between pages — e.g. one demo to another — starts at the top.
  useEffect(() => {
    if (!location.hash) {
      window.scrollTo(0, 0);
    }
  }, [location.pathname]);

  return (
    <div className="App">
      {!hideChrome && <Navigation />}
      <main>
        <Suspense fallback={<div style={{ minHeight: '100vh' }} />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/documentation" element={<DocumentationPage />} />
            <Route path="/enterprise-deployment" element={<EnterpriseDeploymentPage />} />
            <Route path="/blog" element={<BlogListPage />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />
            <Route path="/use-cases" element={<UseCaseIndexPage />} />
            <Route path="/use-cases/:slug" element={<UseCasePage />} />
            <Route path="/demos" element={<DemosIndexPage />} />
            <Route path="/demos/:slug" element={<DemoPage />} />
            <Route path="/video" element={<VideoPlayer />} />
            <Route path="/appundertest/*" element={<AppUnderTest />} />
          </Routes>
        </Suspense>
      </main>
      {!hideChrome && <TidioChat />}
      {!hideChrome && <Footer />}
      {!hideChrome && <CookieBanner />}
      {showSettings && <CookieSettingsModal onClose={closeSettings} />}
    </div>
  );
};

function App() {
  return (
    <HelmetProvider>
      <CookieConsentProvider>
        <Router>
          <AppContent />
        </Router>
      </CookieConsentProvider>
    </HelmetProvider>
  );
}

export default App;
